import { useEffect, useMemo, useRef, useState } from "react";
import type { Column, ConnectionSchema, Relation } from "@/api/queries";
import { Chip } from "@/components/ui/Chip";
import { EmptyState } from "@/components/ui/EmptyState";
import { SearchIcon, TableIcon, ViewIcon } from "@/components/icons";
import { cn } from "@/lib/cn";

function relationLabel(relation: Relation): string {
  return relation.schema_name ? `${relation.schema_name}.${relation.name}` : relation.name;
}

function ColumnTable({ columns }: { columns: Column[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[36rem] border-collapse">
        <thead>
          <tr className="border-b border-border">
            {["Column", "Type", "Nullable", "Default", "Comment"].map((heading) => (
              <th key={heading} className="type-caption text-ink-disabled px-3 py-2 text-start">
                {heading}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {columns.map((column) => (
            <tr key={column.column_name} className="border-b border-border last:border-b-0">
              <td className="px-3 py-2">
                <span className="type-code text-ink">{column.column_name}</span>
                {column.sql_name ? (
                  <span
                    className="type-caption text-ink-disabled ms-2"
                    title="This name needs quoting in SQL"
                  >
                    quote as {column.sql_name}
                  </span>
                ) : null}
              </td>
              <td className="type-code text-ink-secondary px-3 py-2">{column.data_type}</td>
              <td className="type-support text-ink-secondary px-3 py-2">
                {column.is_nullable ? "yes" : "no"}
              </td>
              <td className="type-code text-ink-secondary px-3 py-2">
                {column.column_default ?? "—"}
              </td>
              <td className="type-support text-ink-secondary px-3 py-2">
                {column.column_comment ?? "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Relations and their columns (PRD FR-1.7), read from the server-side schema
 * cache. Native comments show where the engine has them; where it does not,
 * the source page says so and points at the Data Dictionary rather than
 * leaving a column of blanks unexplained.
 */
export function SchemaBrowser({
  schema,
  focus,
}: {
  schema: ConnectionSchema;
  /** A relation to open on arrival — how a SQL step in the trace links here
   *  (PRD FR-5.2). Matched loosely, because the statement may have written a
   *  bare name where the catalog carries a qualified one. */
  focus?: string | null;
}) {
  const [filter, setFilter] = useState("");
  const [openRelation, setOpenRelation] = useState<string | null>(null);
  const focused = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (!focus) return;
    const wanted = focus.toLowerCase();
    const tail = wanted.split(".").pop();
    const match = schema.relations.find((relation) => {
      const label = relationLabel(relation).toLowerCase();
      return label === wanted || relation.name.toLowerCase() === tail;
    });
    if (!match) return;
    setOpenRelation(relationLabel(match));
    // After the row has rendered open, put it where the reader is looking.
    const timer = window.setTimeout(
      () => focused.current?.scrollIntoView({ block: "center", behavior: "smooth" }),
      50,
    );
    return () => window.clearTimeout(timer);
  }, [focus, schema.relations]);

  const relations = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return schema.relations;
    return schema.relations.filter(
      (relation) =>
        relationLabel(relation).toLowerCase().includes(needle) ||
        (relation.comment ?? "").toLowerCase().includes(needle) ||
        (relation.columns ?? []).some((column) =>
          column.column_name.toLowerCase().includes(needle),
        ),
    );
  }, [schema.relations, filter]);

  if (schema.relations.length === 0) {
    return (
      <EmptyState
        icon={<TableIcon className="size-8" />}
        title="No relations yet"
        detail={
          schema.status.error ??
          "No tables or views are readable here. Check the login's SELECT grants, then refresh the schema."
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="relative">
        <span className="text-ink-disabled pointer-events-none absolute start-3 top-1/2 -translate-y-1/2">
          <SearchIcon className="size-4" />
        </span>
        <input
          type="search"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder="Filter relations, comments and columns"
          aria-label="Filter relations"
          className={cn(
            "w-full control-lg rounded-element bg-card text-ink type-body",
            "border border-border ps-9 pe-3 placeholder:text-ink-disabled",
            "hover:border-border-emphasized focus:border-accent motion-fast",
          )}
        />
      </div>

      <ul className="flex flex-col gap-2">
        {relations.map((relation) => {
          const label = relationLabel(relation);
          const open = openRelation === label;
          return (
            <li
              key={label}
              ref={open ? focused : undefined}
              className={cn(
                "rounded-container border bg-card",
                open ? "border-border-emphasized" : "border-border",
              )}
            >
              <button
                type="button"
                onClick={() => setOpenRelation(open ? null : label)}
                aria-expanded={open}
                className="flex w-full items-start gap-3 px-4 py-3 text-start hover:bg-hover motion-fast"
              >
                <span className="text-ink-disabled mt-0.5">
                  {relation.kind === "table" ? (
                    <TableIcon className="size-4" />
                  ) : (
                    <ViewIcon className="size-4" />
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <span className="type-code text-ink">{label}</span>
                    <Chip tone="neutral">{relation.kind}</Chip>
                    {relation.row_estimate !== null && relation.row_estimate !== undefined ? (
                      <span className="type-caption text-ink-disabled tabular-nums">
                        ~{relation.row_estimate.toLocaleString()} rows
                      </span>
                    ) : null}
                  </span>
                  {relation.comment ? (
                    <span className="type-support text-ink-secondary mt-1 block">
                      {relation.comment}
                    </span>
                  ) : null}
                </span>
                <span className="type-caption text-ink-disabled tabular-nums whitespace-nowrap">
                  {relation.column_count ?? 0} cols
                </span>
              </button>
              {open ? (
                <div className="border-t border-border">
                  <div className="flex flex-wrap items-center gap-2 px-4 py-2">
                    <span className="type-caption text-ink-disabled">Query as</span>
                    <code className="type-code text-ink bg-muted rounded-inner px-2 py-1">
                      {relation.query_name}
                    </code>
                  </div>
                  <ColumnTable columns={relation.columns ?? []} />
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>

      {relations.length === 0 ? (
        <p className="type-support text-ink-secondary">Nothing matches that filter.</p>
      ) : null}
    </div>
  );
}
